import React, { useEffect, useState } from 'react';
import axios from '../../utils/axiosConfig';
import Content from '../../components/Content';

// Follow the app theme (light by default, dark via [data-theme]) rather than
// hardcoding colours.
const box = { background: 'var(--white)', border: '1px solid var(--light-grey)', borderRadius: 8, padding: 20, marginBottom: 20, boxShadow: '0 2px 8px rgba(0, 0, 0, 0.06)' };
const input = { width: '100%', padding: '8px 10px', background: 'var(--white)', border: '1px solid var(--grey2)', borderRadius: 6, color: 'var(--black)', marginTop: 4 };
const label = { display: 'block', fontSize: 12, textTransform: 'uppercase', letterSpacing: 1, color: 'var(--grey)', marginBottom: 2 };
const btn = { background: 'var(--primary-color)', color: '#fff', border: 'none', borderRadius: 6, padding: '9px 16px', cursor: 'pointer', fontWeight: 600 };
const th = { textAlign: 'left', padding: '8px 10px', color: 'var(--grey)', fontSize: 12, textTransform: 'uppercase', borderBottom: '1px solid var(--light-grey)' };
const td = { padding: '8px 10px', borderBottom: '1px solid var(--light-grey)', color: 'var(--black)' };
const SUCCESS = '#0f9d58';
const WARN = '#b45309';
const DANGER = '#c81212';

const TIMEZONES = ['UTC', 'America/New_York', 'America/Chicago', 'America/Denver', 'America/Los_Angeles', 'America/Phoenix'];

export default function Companies() {
  const [companies, setCompanies] = useState([]);
  const [form, setForm] = useState({ name: '', slug: '', timezone: 'UTC', notes: '', is_provider: false, ot_licensed: true });
  const [error, setError] = useState('');
  const [msg, setMsg] = useState('');
  const [busy, setBusy] = useState(false);
  const [denied, setDenied] = useState(false);

  const load = async () => {
    try {
      const { data } = await axios.get('/api/companies');
      if (data.status) {
        setCompanies(data.companies || []);
        // Bootstrap: a fresh C2C has no companies. Default the first one to be
        // the provider, so the creating admin becomes provider staff.
        if ((data.companies || []).length === 0) {
          setForm((f) => ({ ...f, is_provider: true }));
        }
      }
    } catch (e) {
      if (e?.response?.status === 403) setDenied(true);
    }
  };
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    setError(''); setMsg(''); setBusy(true);
    try {
      const { data } = await axios.post('/api/companies', form, { headers: { 'Content-Type': 'application/json' } });
      if (data.status) {
        setMsg(`Company "${data.company.name}" created.`);
        setForm({ name: '', slug: '', timezone: 'UTC', notes: '', is_provider: false, ot_licensed: true });
        load();
      } else {
        setError(data.msg || 'Could not create the company.');
      }
    } catch (err) {
      setError(err?.response?.data?.msg || err.message);
    } finally {
      setBusy(false);
    }
  };

  // Toggle OT visibility for a client company (reveal once its baseline is
  // tuned). Provider companies always see OT, so their flag is not toggled here.
  const toggleOt = async (c) => {
    setError(''); setMsg('');
    try {
      const { data } = await axios.patch(`/api/companies/${c.id}`,
        { ot_licensed: !c.ot_licensed }, { headers: { 'Content-Type': 'application/json' } });
      if (data.status) { setMsg(`OT ${!c.ot_licensed ? 'shown to' : 'hidden from'} ${c.name}.`); load(); }
      else setError(data.msg || 'Could not update the company.');
    } catch (err) {
      setError(err?.response?.data?.msg || err.message);
    }
  };

  // Clone the MainGroup master IDS ruleset into an existing company (snapshot).
  // New companies get this automatically at creation; this backfills existing ones.
  const applyMainGroup = async (c) => {
    setError(''); setMsg('');
    if (!window.confirm(`Apply the MainGroup IDS ruleset to ${c.name}? It adds/updates ${c.name}'s rules from the master set (existing rules are kept).`)) return;
    try {
      const { data } = await axios.post(`/api/companies/${c.id}/apply-ids-maingroup`, {}, { headers: { 'Content-Type': 'application/json' } });
      if (data.status) { setMsg(data.msg || `Applied MainGroup to ${c.name}.`); load(); }
      else setError(data.msg || 'Could not apply MainGroup.');
    } catch (err) {
      setError(err?.response?.data?.msg || err.message);
    }
  };

  if (denied) {
    return (
      <Content currentMenu='system' headerTitle='Companies'>
        <div style={{ padding: 24, color: 'var(--grey)' }}>Company management is available to provider staff only.</div>
      </Content>
    );
  }

  return (
    <Content currentMenu='system' headerTitle='Companies'>
      <div style={{ padding: 24, color: 'var(--black)' }}>
        <p style={{ color: 'var(--grey)', marginTop: 0 }}>A company is a tenant. Its accounts and appliances are scoped to it.</p>

        {companies.length === 0 && (
          <div style={{ ...box, borderColor: WARN }}>
            <strong>First-time setup.</strong> Create your provider (support) company first —
            the one whose staff see every client. You&apos;ll join it automatically and can
            then add client companies.
          </div>
        )}
        <div style={box}>
          <h3 style={{ marginTop: 0 }}>Create a Company</h3>
          <form onSubmit={submit}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
              <div><label style={label}>Name</label>
                <input style={input} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Acme Corp" required /></div>
              <div><label style={label}>Slug (optional)</label>
                <input style={input} value={form.slug} onChange={(e) => setForm({ ...form, slug: e.target.value })} placeholder="acme-corp" /></div>
              <div><label style={label}>Timezone</label>
                <select style={input} value={form.timezone} onChange={(e) => setForm({ ...form, timezone: e.target.value })}>
                  {TIMEZONES.map((t) => <option key={t} value={t}>{t}</option>)}
                </select></div>
              <div><label style={label}>Notes (optional)</label>
                <input style={input} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="…" /></div>
            </div>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 14, color: 'var(--black)' }}>
              <input type="checkbox" checked={form.is_provider} onChange={(e) => setForm({ ...form, is_provider: e.target.checked })} />
              Provider company (support staff — sees every client). Only one allowed.
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 10, color: 'var(--black)' }}>
              <input type="checkbox" checked={form.ot_licensed} onChange={(e) => setForm({ ...form, ot_licensed: e.target.checked })} />
              Show OT monitoring to this company&apos;s users. Leave unchecked to hide OT while you tune it — provider staff always see it.
            </label>
            <div style={{ marginTop: 16 }}><button style={btn} type="submit" disabled={busy}>{busy ? 'Creating…' : 'Create Company'}</button></div>
          </form>
          {error && <p style={{ color: DANGER, marginTop: 12 }}>{error}</p>}
          {msg && <p style={{ color: SUCCESS, marginTop: 12 }}>{msg}</p>}
        </div>

        <div style={box}>
          <h3 style={{ marginTop: 0 }}>Companies</h3>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead><tr><th style={th}>Name</th><th style={th}>Slug</th><th style={th}>Timezone</th><th style={th}>Provider</th><th style={th}>OT</th><th style={th}>Appliances</th><th style={th}>Accounts</th><th style={th}>IDS Rules</th></tr></thead>
            <tbody>
              {companies.length === 0 && <tr><td style={{ ...td, color: 'var(--grey)' }} colSpan={8}>No companies yet.</td></tr>}
              {companies.map((c) => (
                <tr key={c.id}>
                  <td style={td}>{c.name}</td>
                  <td style={td}>{c.slug || '—'}</td>
                  <td style={td}>{c.timezone}</td>
                  <td style={td}>{c.is_provider ? 'Yes' : 'No'}</td>
                  <td style={td}>
                    {c.is_provider ? (
                      <span style={{ color: 'var(--grey)' }}>Always</span>
                    ) : (
                      <button
                        onClick={() => toggleOt(c)}
                        style={{ cursor: 'pointer', border: '1px solid var(--grey2)', borderRadius: 6, padding: '3px 10px', fontWeight: 600, background: c.ot_licensed ? SUCCESS : 'var(--white)', color: c.ot_licensed ? '#fff' : 'var(--grey)' }}>
                        {c.ot_licensed ? 'Shown' : 'Hidden'}
                      </button>
                    )}
                  </td>
                  <td style={td}>{c.appliances}</td>
                  <td style={td}>{c.accounts}</td>
                  <td style={td}>
                    {c.is_provider ? (
                      <span style={{ color: 'var(--grey)' }}>—</span>
                    ) : (
                      <button
                        onClick={() => applyMainGroup(c)}
                        title="Clone the MainGroup master IDS ruleset into this company"
                        style={{ cursor: 'pointer', border: '1px solid var(--grey2)', borderRadius: 6, padding: '3px 10px', fontWeight: 600, background: 'var(--white)', color: 'var(--primary-color)' }}>
                        Apply MainGroup
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </Content>
  );
}
